import { registerApplication, start, LifeCycles } from 'single-spa';
import {
  AppConfig,
  ShellComposerBridge,
} from '@robinpowered/dashboard-apps-common';
import { Sentry } from './lib/sentry';
import { AuthBridge } from './lib/authBridge';
import {
  userUploadConfig,
  visitsConfig,
  deliveriesConfig,
  ticketsConfig,
  officeConfig,
  scheduleConfig,
  peopleConfig,
  insightsConfig,
  workplaceDashboardConfig,
  workplaceManagementConfig,
  setupConfig,
  floorPlanManagerConfig,
  layoutToolConfig,
} from './configs';

type AppDefinition = {
  name: string;
  activeWhen: (location: Location) => boolean;
  app: () => Promise<LifeCycles>;
  config: AppConfig;
  isFullScreen?: boolean;
};

// Enable HMR for user-upload app
if (module && module.hot) {
  module.hot.accept();
}

// Track whether we've already started single-spa
let hasStarted = false;

// Global route registry - functions to check if a route is full-screen
export const routeCheckers: Array<(pathname: string) => boolean> = [];

// Setup app route checker: full-screen for all setup routes except /setup/dashboard
routeCheckers.push((pathname: string) => {
  // Check if this is a setup route
  if (pathname.includes('/setup/') || pathname.includes('/onboarding/')) {
    // Exclude /setup/dashboard and /setup/manage from full-screen
    if (
      pathname.includes('/setup/dashboard') ||
      pathname.includes('/setup/settings')
    ) {
      return false;
    }
    // All other setup routes are full-screen
    return true;
  }

  // Deliveries magic-link confirm/reject routes should be full-screen
  if (pathname.startsWith('/magic/deliveries/')) {
    return true;
  }

  return false;
});

const isApplicationActive = (location: Location, basePath: string) =>
  location.pathname.split('/')[2] === basePath;

// Apps to be registered
export const apps: AppDefinition[] = [
  {
    name: 'deliveries',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'deliveries'),
    app: (): Promise<LifeCycles> => import('deliveries'),
    config: deliveriesConfig,
  },
  {
    name: 'tickets',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'tickets'),
    app: (): Promise<LifeCycles> => import('tickets'),
    config: ticketsConfig,
  },
  {
    name: 'user-upload',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'user-upload'),
    app: (): Promise<LifeCycles> => import('user-upload'),
    config: userUploadConfig,
    isFullScreen: true,
  },
  {
    name: 'visits',
    activeWhen: (location: Location) => isApplicationActive(location, 'visits'),
    app: (): Promise<LifeCycles> => import('visits'),
    config: visitsConfig,
  },
  {
    name: 'office',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'officeV2'),
    app: (): Promise<LifeCycles> => import('office'),
    config: officeConfig,
  },
  {
    name: 'schedule',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'schedule'),
    app: (): Promise<LifeCycles> => import('schedule'),
    config: scheduleConfig,
  },
  {
    name: 'people',
    activeWhen: (location: Location) => isApplicationActive(location, 'people'),
    app: (): Promise<LifeCycles> => import('people'),
    config: peopleConfig,
  },
  {
    name: 'insights',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'insights'),
    app: (): Promise<LifeCycles> => import('insights'),
    config: insightsConfig,
  },
  {
    name: 'workplace-dashboard',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'workplace-dashboard'),
    app: (): Promise<LifeCycles> => import('workplace-dashboard'),
    config: workplaceDashboardConfig,
  },
  {
    name: 'workplace-management',
    // Activate for ALL workplace-management routes (exact route + sub-routes)
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'workplace-management'),
    app: async (): Promise<LifeCycles> => {
      const module = await import('workplace-management');
      // Register the app's route checker function
      if (module.isFullScreenRoute) {
        routeCheckers.push(module.isFullScreenRoute);
      }
      return module;
    },
    config: workplaceManagementConfig,
  },
  {
    name: 'setup',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'setup') ||
      // NOTE: to support old 'onboarding' routes from robin-dashboard we added
      // them to setup app
      location.pathname.includes('/onboarding/'),
    app: (): Promise<LifeCycles> => import('setup'),
    config: setupConfig,
  },
  {
    name: 'floor-plan-manager',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'floor-plan-manager'),
    app: async (): Promise<LifeCycles> => import('floor-plan-manager'),
    config: floorPlanManagerConfig,
    isFullScreen: true,
  },
  {
    name: 'layout-tool',
    activeWhen: (location: Location) =>
      isApplicationActive(location, 'manage-mapV2'),
    app: async (): Promise<LifeCycles> => import('layout-tool'),
    config: layoutToolConfig,
    isFullScreen: true,
  },
];

/**
 * Name of the MFE whose `activeWhen` predicate matches the given pathname,
 * if any. This consults the same registry single-spa uses to mount apps, so
 * it stays correct even when an app's route segment differs from its name
 * (e.g. `office` activates on `officeV2`, `setup` also on `/onboarding/*`)
 * or the routing structure changes.
 */
export const getActiveAppNameForPathname = (
  pathname: string
): string | undefined =>
  apps.find((app) => app.activeWhen({ ...window.location, pathname }))?.name;

// Function to register applications when DOM is ready
export const registerApplications = (
  bridge: AuthBridge,
  composerBridge: ShellComposerBridge
) => {
  // Prevent calling start() multiple times (React StrictMode calls effects twice in dev)
  if (hasStarted) {
    return;
  }

  apps.forEach(({ name, activeWhen, app, config }) => {
    try {
      registerApplication({
        name,
        app,
        activeWhen,
        customProps: () => ({
          config,
          // Pass the authBridge object reference so apps can read auth data
          // synchronously (if already resolved) or subscribe to updates.
          authBridge: bridge,
          // Same object reference for the event-composer bridge so MFEs share
          // the shell's single pub/sub instance (see ShellComposerBridge).
          composerBridge,
          domElement: document.getElementById('singleSpaRoot'),
        }),
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  });

  start();
  hasStarted = true;
};
